import { forwardRef, type ComponentPropsWithoutRef } from "react";

export const PROJECT_FILE_PROXY_PATH = "/api/files";

function getProjectFileHref(reference?: string | null): string | undefined {
  if (!reference) {
    return undefined;
  }

  if (reference.startsWith("automerge:")) {
    return undefined;
  }

  if (reference.startsWith(PROJECT_FILE_PROXY_PATH)) {
    return reference;
  }

  if (reference.startsWith("s3://")) {
    return `${PROJECT_FILE_PROXY_PATH}?source=${encodeURIComponent(reference)}`;
  }

  return reference;
}

export type ProjectFileLinkProps = Omit<
  ComponentPropsWithoutRef<"a">,
  "href"
> & {
  reference?: string | null;
  fallbackHref?: string;
};

export const ProjectFileLink = forwardRef<
  HTMLAnchorElement,
  ProjectFileLinkProps
>(({ reference, fallbackHref = "#", target, rel, ...props }, ref) => {
  const resolvedHref = getProjectFileHref(reference) ?? fallbackHref;
  const isExternalLink =
    resolvedHref !== "#" && !resolvedHref.startsWith("automerge:");
  const finalTarget = target ?? (isExternalLink ? "_blank" : undefined);
  const finalRel = rel ?? (isExternalLink ? "noreferrer noopener" : undefined);

  return (
    <a
      ref={ref}
      href={resolvedHref}
      target={finalTarget}
      rel={finalRel}
      {...props}
    />
  );
});

export default ProjectFileLink;
